import React from "react";
import { clearBookingDrafts } from "../../utils/bookingDraft";
import { Alert, Button, Col, Flex, notification, Row, Space, Spin } from "antd";
import { useParams, useSearchParams } from "react-router-dom";
import PricingSummary from "../../components/shared/PricingSummary";
import B2C2BApi from "../../api-services/b2c2b-api.service";
import { BookingTransaction, GroundTourTransaction } from "../../interfaces/booking.interface";
import PaymentSuccessV3 from "../../components/shared/v3/PaymentSuccessV3";
import GroundTourPaymentStatus from "./ground-tour/GroundTourPaymentStatus";

const payment = {
  "paid": {
    header: "Payment Successful",
    message: "Thank you for your booking. A confirmation email, including a secure link to your booking information, has been sent to your inbox.",
    buttons: () => (
              <div className="payment-status_action-buttons">
                <Button color="primary" variant="solid" href="/web">
                  Back to Home
                </Button>
              </div>
            )
  },
  "pending": {
    header: "Pending Payment",
    message: "Payment is currently pending. Your booking will not be confirmed until the payment is successfully processed.",
    buttons: ({countdown}) => (
      <div className="payment-status_action-buttons" style={{flexDirection: 'column'}}>
        <b>
          We're updating your payment status. Please retry in {countdown} seconds...
        </b>

        <Button color="primary" variant="solid" href="/web">
          Back to Home
        </Button>
      </div>
    )
  },
  "failed": {
    header: "Payment Unsuccessful",
    message: "Your payment could not be completed. Your booking has not been confirmed—please retry the payment process to secure your reservation.",
    buttons: ({retryFn}) => (
              <div className="payment-status_action-buttons">
                <Button onClick={retryFn} color="primary" variant="solid">
                  Retry Payment
                </Button>
                <Button color="primary" variant="outlined" href="/web">
                  Back to Home
                </Button>
              </div>
            )
  },
  "unavailable": {
    header: "Payment Details Unavailable",
    message: "We could not verify this payment. Please use the secure payment result link sent to you or sign in and try again.",
    buttons: () => (
              <div className="payment-status_action-buttons">
                <Button color="primary" variant="solid" href="/web">
                  Back to Home
                </Button>
              </div>
            )
  }
}

interface IPaymentStatusState {
  paymentStatus: {header: string, message: string, buttons: Function} | null;
  booking: BookingTransaction | null;
  groundTour: GroundTourTransaction | null;
  loading: boolean;
  countdown: number;
  isV3Payment: boolean;
}


function withParams(Component) {
  return props => <Component {...props} params={useParams()} searchParams={useSearchParams()}/>;
}


class PaymentStatus extends React.Component <{params, searchParams}, IPaymentStatusState>  {
  state:IPaymentStatusState = {
    paymentStatus: null,
    booking: null,
    groundTour: null,
    loading: true,
    countdown: 30,
    isV3Payment: false
  }

  countdownInterval: number | null = null;


  componentDidMount() {
    this.fetchBookingInfo();
  }

  componentWillUnmount() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  }

  fetchBookingInfo = () => {
    const [searchParams] = this.props.searchParams;
    const token = searchParams.get("token");
    
    B2C2BApi.getBookingInfoByPaymentId(this.props.params.id, token)
      .then(result => {
        if ("kind" in result) {
          this.setState({groundTour: result, loading: false}, () => this.syncPolling(result.status));
          return;
        }
        const booking = result;
        if (booking.status === "paid") clearBookingDrafts();
        const paymentStatus = payment[booking.status] || payment["failed"];
        // Scope the V3 payment flag to this booking so it can't leak into
        // another booking's payment status page within the same session.
        const storedV3 = sessionStorage.getItem('ice_v3_payment');
        let isV3Payment = false;
        if (storedV3 === '1') {
          sessionStorage.setItem('ice_v3_payment', String(booking.bookingId));
          isV3Payment = true;
        } else if (storedV3 === String(booking.bookingId)) {
          isV3Payment = true;
        }
        this.setState({paymentStatus, booking, loading: false, isV3Payment}, () => this.syncPolling(booking.status));
      })
      .catch(err => {
        console.log(err)
        this.setState({loading: false, paymentStatus: payment["unavailable"]});
        notification.error({ message: err?.response?.data?.error || 'Failed to fetch booking information.'});
      });
  }

  syncPolling = (status: string) => {
    if (status === "pending") {
      this.startPolling();
    } else {
      // Clear any existing intervals if status changed
      if (this.countdownInterval) clearInterval(this.countdownInterval);
    }
  }

  startPolling = () => {
    // Clear any existing interval before starting a new one to prevent memory leaks.
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Start countdown timer
    this.setState({ countdown: 30 });
    
    this.countdownInterval = setInterval(() => {
      this.setState(prevState => ({
        countdown: prevState.countdown - 1
      }), () => {
        if (this.state.countdown === 0) {
          clearInterval(this.countdownInterval!);
          this.fetchBookingInfo();
        }
      });
    }, 1000);
  }

  retryPayment = () => {
    const [searchParams] = this.props.searchParams;
    const token = searchParams.get("token");

    const {groundTour} = this.state;
    if (groundTour) {
      B2C2BApi.retryGroundTourPayment(groundTour.bookingId, token)
        .then((data) => {
          location.href = data.redirect_to;
        })
        .catch(err => {
          notification.error({ message: err?.response?.data?.error || 'An error occured while retrying payment.'});
        });
      return;
    }

    if (!this.state.booking) {
      notification.error({ message: "Booking details not found. Cannot retry payment." });
      return;
    }

    const {bookingId} = this.state.booking;    
    B2C2BApi.retryPayment(bookingId, token)
      .then((data) => {
        if (this.state.isV3Payment) {
          sessionStorage.setItem('ice_v3_payment', String(bookingId));
        }
        location.href = data.redirect_to;
      })
      .catch(err => {
        notification.error({ message: 'An error occured while retrying payment.'});
      });
  }


  render(){
    const {booking, paymentStatus, countdown, groundTour} = this.state;

    if (groundTour) {
      return <GroundTourPaymentStatus transaction={groundTour} countdown={countdown} onRetry={this.retryPayment} />;
    }

    const partiallyPaid = booking?.paymentStatus === "Partial Paid";

    const { isV3Payment } = this.state;
    if (!this.state.loading && booking && isV3Payment && window.innerWidth < 600) {
      return <PaymentSuccessV3
        transaction={booking}
        status={booking.status as "paid" | "pending" | "failed"}
        countdown={countdown}
        onRetry={this.retryPayment}
      />;
    }

    return (
      <div className="payment-status">
        <Spin spinning={this.state.loading}>
          <h1> {paymentStatus?.header} </h1>
          <div className="payment-status_content">
            <p className="payment-status_content-message"> {paymentStatus?.message} </p>
            <div className="payment-status_content-payment-details"> 
              <Row gutter={[20, 20]}>
                <Col xs={24} sm={24} lg={12}>
                  <div className="payment-status_content-payment-details-fee">
                    <label>Booking Fee</label>
                    <span> {booking?.bookingFee} </span>
                  </div>
                </Col>                    
                <Col xs={24} sm={24} lg={12}>
                  <div className="payment-status_content-payment-details-transaction">
                    <div>
                      <span> Payment Method: {booking?.paymentMethod} </span>
                      <span> Time of transaction: {booking?.transactionDate} </span>
                    </div>
                    <div>
                      <span>Payment Status:</span>
                      <span className={`status-badge ${booking?.status.toLowerCase()}`}> {booking?.status} </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            { booking?.summary && (
              <>
                {
                  partiallyPaid && (
                    <div className="outstanding-balance">
                      <Flex justify="space-between" wrap align="flex-start" gap="large">
                        <Space direction="vertical" size={5}>
                          <span>Outstanding balance</span>
                          <span className="amount">{booking?.summary.currencyBalance}</span>
                        </Space>
                        <Space direction="vertical" size={5} className="due-date">
                          <span>Final payment due before</span>
                          <span>{booking?.summary.finalPaymentDate}</span>
                        </Space>
                      </Flex>
                    </div>
                  )
                }

                <PricingSummary 
                  header="Booking Details" 
                  serviceFee={booking?.serviceFee} 
                  summary={booking?.summary} 
                  paymentMethod={booking?.paymentMethod}
                  refNo={booking?.refNo}
                  showBalance={partiallyPaid}
                  />
              </>
            )
            }
          </div>
          {paymentStatus?.buttons({countdown, retryFn: this.retryPayment})}
        </Spin>
      </div>
    )
  }
  
}


export default withParams(PaymentStatus);
